import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api/client';
import { useToast } from '../components/Toast';

interface Props {
  onCreated: (projectId: string) => void;
  onCancel: () => void;
}

// Kits are static for v1 — list them here. When kit registry exposes an
// /api/kits endpoint we can fetch dynamically.
const KIT_OPTIONS = [
  { id: 'roofing-premium-v1', label: 'Roofing Premium v1' },
];

const PLACEHOLDER_BRAND_JSON = `{
  "id": "your-brand-slug",
  "business": {
    "name": "Your Business Name",
    "tagline": "Catchy tagline",
    "description": "What your business does and who it serves.",
    "yearsExperience": 10,
    "openingHours": {
      "monday": "09:00-17:00",
      "tuesday": "09:00-17:00",
      "wednesday": "09:00-17:00",
      "thursday": "09:00-17:00",
      "friday": "09:00-17:00"
    },
    "ctaText": "Get a Free Estimate"
  },
  "contact": {
    "phone": "+19999999999",
    "email": "hello@example.com",
    "address": { "line1": "...", "city": "...", "state": "NY", "postalCode": "10000", "country": "US" }
  },
  "branding": { "primaryColor": "#0066cc", "logoUrl": "/assets/images/logo.png" },
  "coverage": { "boroughs": ["Manhattan"], "neighborhoods": [] },
  "services": ["Service A", "Service B"],
  "leadCapture": { "destinationEmail": "leads@example.com" }
}`;

export function CreateSiteScreen({ onCreated, onCancel }: Props) {
  const toast = useToast();
  const [kitId, setKitId] = useState(KIT_OPTIONS[0]!.id);
  const [brandJson, setBrandJson] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  function tryParse(): unknown | null {
    setParseError(null);
    try {
      const v = JSON.parse(brandJson);
      if (typeof v !== 'object' || v === null) {
        setParseError('Brand JSON must be an object');
        return null;
      }
      return v;
    } catch (e) {
      setParseError(`Invalid JSON: ${(e as Error).message}`);
      return null;
    }
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const brand = tryParse();
    if (!brand) return;
    setSubmitting(true);
    try {
      const r = await api.createProject({ brand, kitId });
      if (r.warnings && r.warnings.length > 0) {
        toast.show(`Created with ${r.warnings.length} warning(s) — see project detail`, 'info');
      } else {
        toast.success('Project created');
      }
      onCreated(r.projectId);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function loadTemplate(): void {
    setBrandJson(PLACEHOLDER_BRAND_JSON);
    setParseError(null);
  }

  function handlePaste(): void {
    navigator.clipboard.readText().then((t) => setBrandJson(t)).catch(() => {
      toast.error('Could not read clipboard — paste manually');
    });
  }

  return (
    <div className="max-w-3xl">
      <button onClick={onCancel} className="text-sm text-gray-600 hover:text-gray-900 mb-4">
        ← Back
      </button>

      <h1 className="text-2xl font-semibold mb-1">Create a new site</h1>
      <p className="text-sm text-gray-500 mb-6">
        Paste the brand JSON below. The engine handles research, content, images, and the
        live preview. This usually takes 30-45 minutes the first time.
      </p>

      <form onSubmit={onSubmit} className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Site kit</span>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
            value={kitId}
            onChange={(e) => setKitId(e.target.value)}
            disabled={submitting}
          >
            {KIT_OPTIONS.map((k) => (
              <option key={k.id} value={k.id}>{k.label}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <div className="flex items-center justify-between mb-1">
            <span className="block text-sm font-medium text-gray-700">Brand JSON</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={loadTemplate}
                className="text-xs text-brand hover:text-brand-dark underline"
              >
                Load template
              </button>
              <button
                type="button"
                onClick={handlePaste}
                className="text-xs text-brand hover:text-brand-dark underline"
              >
                Paste from clipboard
              </button>
            </div>
          </div>
          <textarea
            className="w-full h-96 px-3 py-2 border border-gray-300 rounded-md font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand"
            value={brandJson}
            onChange={(e) => { setBrandJson(e.target.value); setParseError(null); }}
            placeholder={PLACEHOLDER_BRAND_JSON}
            spellCheck={false}
            required
            disabled={submitting}
          />
        </label>

        {parseError && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-800 text-sm rounded-md">
            {parseError}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting || !brandJson.trim()}
            className="bg-brand text-white font-medium px-5 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {submitting ? 'Creating…' : 'Create site'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="text-gray-700 font-medium px-5 py-2 rounded-md hover:bg-gray-100"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
